import React, { useState, useEffect, useCallback, useRef, memo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { saveWrongAnswer } from '../../services/d1Api';

// ─── Timer Bar ────────────────────────────────────────────────────────────────
const TimerBar = memo(({ totalSeconds, remaining, paused }) => {
  const pct = totalSeconds > 0 ? (remaining / totalSeconds) * 100 : 100;
  const color = pct > 50 ? 'bg-white' : pct > 20 ? 'bg-gray-300' : 'bg-gray-500';
  const mins = Math.floor(remaining / 60);
  const secs = remaining % 60;

  return (
    <div className="glass-panel p-4 flex items-center gap-4">
      <div className="flex-1 h-3 bg-white/10 rounded-full overflow-hidden">
        <motion.div
          className={`h-full ${color} rounded-full`}
          animate={{ width: `${pct}%` }}
          transition={{ duration: 0.5 }}
        />
      </div>
      <div className="flex items-center gap-2">
        {paused && <span className="text-xs text-gray-300 font-bold animate-pulse">⏸ متوقف</span>}
        <span className="font-mono font-bold text-lg text-white min-w-[60px] text-center">
          {String(mins).padStart(2, '0')}:{String(secs).padStart(2, '0')}
        </span>
      </div>
    </div>
  );
});
TimerBar.displayName = 'TimerBar';

// ─── Option Colors ────────────────────────────────────────────────────────────
const optionStyles = {
  a: { bg: 'bg-white/5 border-white/10 hover:bg-white/10', selected: 'bg-white/20 border-white/40', icon: 'أ' },
  b: { bg: 'bg-white/5 border-white/10 hover:bg-white/10', selected: 'bg-white/20 border-white/40', icon: 'ب' },
  c: { bg: 'bg-white/5 border-white/10 hover:bg-white/10', selected: 'bg-white/20 border-white/40', icon: 'ج' },
  d: { bg: 'bg-white/5 border-white/10 hover:bg-white/10', selected: 'bg-white/20 border-white/40', icon: 'د' },
};

// ═══════════════════════════════════════════════════════════════════════════════
// ███  QUIZ PLAYER
// ═══════════════════════════════════════════════════════════════════════════════
export const QuizPlayer = ({
  quiz,
  questions = [],
  userId,
  onFinish,
  studentPhone = '',
  // ── Read-only review mode ──
  readOnly = false,
  previousAnswers = {},   // { questionId: 'a'|'b'|'c'|'d' }
  previousScore = 0,
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [showResult, setShowResult] = useState(false);
  const [answers, setAnswers] = useState(readOnly ? previousAnswers : {});
  const [score, setScore] = useState(readOnly ? previousScore : 0);
  const [finished, setFinished] = useState(readOnly); // readOnly starts "finished" so we show review
  const [paused, setPaused] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState((quiz.duration_minutes || 0) * 60);
  const timerRef = useRef(null);

  // ── Read-only: jump straight to review mode ──
  const [reviewMode, setReviewMode] = useState(readOnly);

  const hasDuration = (quiz.duration_minutes || 0) > 0;
  const totalSeconds = (quiz.duration_minutes || 0) * 60;
  const canPause = Number(quiz.allow_pause) === 1;
  const showResultImmediate = Number(quiz.show_result) === 1;
  const currentQuestion = questions[currentIndex];
  const totalQuestions = questions.length;

  // ── Timer (only in play mode) ──
  useEffect(() => {
    if (readOnly || !hasDuration || finished || paused) return;
    timerRef.current = setInterval(() => {
      setTimeRemaining(prev => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          handleFinishQuiz();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [hasDuration, finished, paused, readOnly]);

  const handleSelectAnswer = (key) => {
    if (readOnly || showResult || finished) return;
    setSelectedAnswer(key);
  };

  const handleConfirmAnswer = useCallback(async () => {
    if (!selectedAnswer || !currentQuestion || readOnly) return;

    const isCorrect = selectedAnswer === currentQuestion.correct_answer;
    const newAnswers = { ...answers, [currentQuestion.id]: selectedAnswer };
    setAnswers(newAnswers);

    if (isCorrect) {
      setScore(s => s + 1);
    } else {
      saveWrongAnswer({
        user_id: userId,
        quiz_id: quiz.id,
        question_id: currentQuestion.id,
        question_text: currentQuestion.question_text,
        user_answer: selectedAnswer,
        correct_answer: currentQuestion.correct_answer,
        option_a: currentQuestion.option_a,
        option_b: currentQuestion.option_b,
        option_c: currentQuestion.option_c,
        option_d: currentQuestion.option_d,
      });
    }

    if (showResultImmediate) {
      setShowResult(true);
      setTimeout(() => {
        setShowResult(false);
        setSelectedAnswer(null);
        if (currentIndex < totalQuestions - 1) {
          setCurrentIndex(i => i + 1);
        } else {
          handleFinishQuiz(newAnswers);
        }
      }, 1500);
    } else {
      setSelectedAnswer(null);
      if (currentIndex < totalQuestions - 1) {
        setCurrentIndex(i => i + 1);
      } else {
        handleFinishQuiz(newAnswers);
      }
    }
  }, [selectedAnswer, currentQuestion, currentIndex, totalQuestions, answers, userId, quiz, showResultImmediate, readOnly]);

  const handleFinishQuiz = (finalAnswers) => {
    clearInterval(timerRef.current);
    setFinished(true);
    const finalScore = finalAnswers
      ? Object.entries(finalAnswers).filter(([qId, ans]) => {
          const q = questions.find(q => q.id === qId);
          return q && ans === q.correct_answer;
        }).length
      : score;
    setScore(finalScore);
  };

  // ── READ-ONLY REVIEW MODE ──
  // Shows all questions in a scrollable list with user answers highlighted
  if (reviewMode && readOnly) {
    const pct = totalQuestions > 0 ? Math.round((previousScore / totalQuestions) * 100) : 0;
    const optionKeys = ['a', 'b', 'c', 'd'];

    return (
      <div className="space-y-6" dir="rtl">
        {/* Score banner */}
        <div className="glass-panel p-6 text-center">
          <p className="text-slate-400 text-sm mb-1">نتيجتك في هذا الامتحان</p>
          <div className="text-4xl font-black text-white mb-1">{previousScore} / {totalQuestions}</div>
          <div className="w-full max-w-xs mx-auto h-3 bg-white/10 rounded-full overflow-hidden mt-2">
            <div className={`h-full rounded-full ${pct >= 60 ? 'bg-white' : 'bg-gray-400'}`} style={{ width: `${pct}%` }} />
          </div>
          <p className={`mt-2 font-bold ${pct >= 60 ? 'text-emerald-400' : 'text-red-400'}`}>{pct}%</p>
          <p className="text-xs text-gray-400 mt-2">📝 وضع المراجعة — لقد أديت هذا الامتحان مسبقاً</p>
        </div>

        {/* All questions */}
        {questions.map((q, idx) => {
          const userAnswer = previousAnswers[q.id];
          return (
            <div key={q.id} className="glass-panel p-6">
              <div className="flex items-start gap-3 mb-4">
                <span className="w-9 h-9 rounded-lg bg-white/10 text-gray-300 flex items-center justify-center text-sm font-bold shrink-0">
                  {idx + 1}
                </span>
                <h3 className="text-lg font-bold text-white leading-relaxed">{q.question_text}</h3>
              </div>
              <div className="space-y-2">
                {optionKeys.map((key) => {
                  const optText = q[`option_${key}`];
                  if (!optText) return null;
                  const isCorrectAnswer = key === q.correct_answer;
                  const isUserAnswer = key === userAnswer;
                  const style = optionStyles[key];

                  let className = 'w-full p-3 rounded-xl border-2 text-right flex items-center gap-3 ';
                  if (isCorrectAnswer) {
                    className += 'bg-emerald-500/20 border-emerald-500/50 text-emerald-300';
                  } else if (isUserAnswer && !isCorrectAnswer) {
                    className += 'bg-red-500/20 border-red-500/50 text-red-300 line-through';
                  } else {
                    className += 'bg-white/5 border-white/5 text-slate-500';
                  }

                  return (
                    <div key={key} className={className}>
                      <span className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center font-black text-sm shrink-0">{style.icon}</span>
                      <span className="flex-1 font-bold text-sm">{optText}</span>
                      {isCorrectAnswer && <span className="text-lg">✓</span>}
                      {isUserAnswer && !isCorrectAnswer && <span className="text-xs">إجابتك ✗</span>}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}

        <button
          onClick={() => onFinish?.()}
          className="w-full py-4 bg-white text-black font-black rounded-2xl text-lg transition hover:bg-gray-200"
        >
          العودة للكورس
        </button>
      </div>
    );
  }

  // ── Finished Screen (normal play mode — NOT read-only) ──
  if (finished && !readOnly) {
    const pct = totalQuestions > 0 ? Math.round((score / totalQuestions) * 100) : 0;
    const grade = pct >= 90 ? 'ممتاز 🌟' : pct >= 75 ? 'جيد جداً 👏' : pct >= 60 ? 'جيد 👍' : pct >= 50 ? 'مقبول' : 'حاول مرة أخرى 💪';

    return (
      <div className="space-y-6">
        <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="glass-panel p-8 text-center">
          <div className="text-6xl mb-4">{pct >= 60 ? '🎉' : '📚'}</div>
          <h2 className="text-3xl font-black text-white mb-2">نتيجة الامتحان</h2>
          <p className="text-slate-400 mb-6">{quiz.title}</p>

          <div className="inline-flex items-center gap-3 bg-white/5 px-8 py-4 rounded-2xl mb-6">
            <span className="text-5xl font-black text-white">{score}</span>
            <span className="text-2xl text-slate-400">/</span>
            <span className="text-3xl text-slate-300">{totalQuestions}</span>
          </div>

          <div className="mb-4">
            <div className="w-full h-4 bg-white/10 rounded-full overflow-hidden max-w-sm mx-auto">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${pct}%` }}
                transition={{ duration: 1.5, ease: 'easeOut' }}
                className={`h-full rounded-full ${pct >= 60 ? 'bg-white' : 'bg-gray-400'}`}
              />
            </div>
            <p className="text-2xl font-bold text-white mt-3">{pct}%</p>
          </div>

          <p className={`text-xl font-bold ${pct >= 60 ? 'text-emerald-400' : 'text-red-400'}`}>{grade}</p>

          <div className="flex gap-4 justify-center mt-8">
            <button
              onClick={() => onFinish?.({ score, total: totalQuestions, answers })}
              className="px-8 py-3 bg-white text-black font-bold rounded-xl transition hover:bg-gray-200"
            >
              العودة للكورس
            </button>
          </div>
        </motion.div>
      </div>
    );
  }

  if (!currentQuestion) return <div className="glass-panel p-12 text-center text-white">لا توجد أسئلة في هذا الامتحان</div>;

  const optionKeys = ['a', 'b', 'c', 'd'];

  return (
    <div className="space-y-5" dir="rtl">
      {/* Timer — hidden in readOnly */}
      {hasDuration && !readOnly && (
        <TimerBar totalSeconds={totalSeconds} remaining={timeRemaining} paused={paused} />
      )}

      {/* Progress */}
      <div className="glass-panel p-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm text-slate-400">السؤال {currentIndex + 1} من {totalQuestions}</span>
          <span className="text-sm font-bold text-white">{quiz.title}</span>
          {canPause && hasDuration && !readOnly && (
            <button
              onClick={() => setPaused(p => !p)}
              className={`text-xs px-3 py-1 rounded-lg font-bold transition ${paused ? 'bg-white/10 text-white' : 'bg-white/5 text-gray-300'}`}
            >
              {paused ? '▶ استكمال' : '⏸ إيقاف مؤقت'}
            </button>
          )}
        </div>
        <div className="w-full h-2 bg-white/10 rounded-full overflow-hidden">
          <motion.div
            className="h-full bg-white rounded-full"
            animate={{ width: `${((currentIndex + 1) / totalQuestions) * 100}%` }}
          />
        </div>
      </div>

      {/* Question */}
      <AnimatePresence mode="wait">
        <motion.div
          key={currentQuestion.id}
          initial={{ opacity: 0, x: 30 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -30 }}
          className="glass-panel p-8"
        >
          <h3 className="text-xl font-bold text-white leading-relaxed mb-8">
            {currentQuestion.question_text}
          </h3>

          <div className="space-y-3">
            {optionKeys.map((key) => {
              const optText = currentQuestion[`option_${key}`];
              if (!optText) return null;

              const style = optionStyles[key];
              const isSelected = selectedAnswer === key;
              const isCorrectAnswer = key === currentQuestion.correct_answer;

              let className = `w-full p-4 rounded-xl border-2 text-right transition-all duration-300 flex items-center gap-4 `;
              if (showResult && isCorrectAnswer) {
                className += 'bg-emerald-500/30 border-emerald-500 text-white';
              } else if (showResult && isSelected && !isCorrectAnswer) {
                className += 'bg-red-500/30 border-red-500 text-white';
              } else if (isSelected) {
                className += style.selected + ' text-white';
              } else {
                className += style.bg + ' text-slate-200';
              }

              return (
                <motion.button
                  key={key}
                  onClick={() => handleSelectAnswer(key)}
                  whileHover={!showResult && !readOnly ? { scale: 1.01 } : {}}
                  whileTap={!showResult && !readOnly ? { scale: 0.99 } : {}}
                  className={className}
                  disabled={showResult || paused || readOnly}
                >
                  <span className={`w-10 h-10 rounded-lg flex items-center justify-center font-black text-lg shrink-0 ${isSelected ? 'bg-white/20' : 'bg-white/5'}`}>
                    {style.icon}
                  </span>
                  <span className="flex-1 font-bold text-base">{optText}</span>
                  {showResult && isCorrectAnswer && <span className="text-2xl">✓</span>}
                  {showResult && isSelected && !isCorrectAnswer && <span className="text-2xl">✗</span>}
                </motion.button>
              );
            })}
          </div>
        </motion.div>
      </AnimatePresence>

      {/* Confirm Button — hidden in readOnly */}
      {!showResult && !readOnly && (
        <motion.button
          onClick={handleConfirmAnswer}
          disabled={!selectedAnswer || paused}
          whileHover={{ scale: selectedAnswer ? 1.02 : 1 }}
          whileTap={{ scale: 0.98 }}
          className={`w-full py-4 rounded-2xl font-black text-lg transition-all ${
            selectedAnswer && !paused
              ? 'bg-white text-black shadow-lg'
              : 'bg-white/5 text-slate-500 cursor-not-allowed'
          }`}
        >
          {currentIndex < totalQuestions - 1 ? 'تأكيد والسؤال التالي ←' : 'تأكيد وإنهاء الامتحان'}
        </motion.button>
      )}
    </div>
  );
};
